import { FC, useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { Analytics as AnalyticsIcon } from "@mui/icons-material";
import { useIntl } from "react-intl";
import { useDebouncedCallback } from "use-debounce";
import { json2csv } from "json-2-csv";

import { ITemplate, ITemplateRow, RowControlType } from "@framework/types";
import { useAsu } from "@framework/provider-asu";
import { FormWrapper } from "@framework/mui-form";
import { ProgressOverlay } from "@framework/mui-page-layout";
import { useApiCall } from "@framework/react-hooks";

import { EmptyPlaceholder } from "../../../../components/common/empty-placeholder";
import { EXCEL_CSV_DELIMITER } from "../../../../utils/download-csv";
import { downloadZip, IFile } from "../../../../utils/download-zip";
import { AnalyticsExport } from "./components/analytics-export";
import { TemplateInput } from "./components/inputs/template";
import { ControlsInput } from "./components/inputs/controls";
import { ClearButton } from "./components/buttons/clear";
import { emptyAnalytics } from "./empty";
import { SubmitButton } from "./components/buttons/submit";
import { ViewSelector } from "./components/selector";
import {
  Root,
  StyledDisplayView,
  StyledSettings,
  StyledSettingsButtons,
  StyledSettingsColumn,
  StyledSettingsWrapper,
} from "./styled";

interface IValues {
  topN?: number;
  [key: string]: any;
}

export const Analytics: FC = () => {
  const { selectedItem, startTimestamp, endTimestamp, isLoading } = useAsu();

  const { formatMessage } = useIntl();

  const [template, setTemplate] = useState<ITemplate | null>(null);
  const [initialValues, setInitialValues] = useState(emptyAnalytics);
  const [templates, setTemplates] = useState<ITemplateRow[] | null>(null);
  const [values, setValues] = useState<IValues | null>(null);
  const [validateControls, setValidateControls] = useState<(() => boolean) | null>(null);
  const chartsRef = useRef<Map<string, { name: string; input: Blob }>>(new Map());

  const selectedItemId = selectedItem && ("Id" in selectedItem ? selectedItem.Id : selectedItem.TrainId);
  const selectedItemType = selectedItem && ("Id" in selectedItem ? 1 : 2);
  const equipmentContextKey =
    selectedItemId != null && selectedItemType != null ? `${selectedItemId}:${selectedItemType}` : "";
  const [formContextKey, setFormContextKey] = useState(equipmentContextKey);

  const resetAnalyticsState = () => {
    setValues(null);
    setTemplate(null);
    setTemplates(null);
    setInitialValues(emptyAnalytics);
    chartsRef.current = new Map();
    setValidateControls(null);
  };

  if (equipmentContextKey && equipmentContextKey !== formContextKey) {
    setFormContextKey(equipmentContextKey);
    resetAnalyticsState();
  }

  const handleValidationChange = (validateFn: () => boolean) => {
    setValidateControls(() => validateFn);
  };

  const handleControlValueReset = (controlName: string, value: any) => {
    setInitialValues(prev => {
      const newValues = {
        ...prev,
        [controlName]: value,
      };
      // Также нужно сбросить форму с новыми значениями
      // Это будет сделано в useEffect ниже
      return newValues;
    });
  };

  const { fn: executeQuery, isLoading: isQueryExecuting } = useApiCall<void>(
    async (api, values: any) => {
      const { templateId, chartView: _, layoutView: __, ...additionalParams } = values;
      setTemplates(null);
      const json = (await api.fetchJson({
        url: `/template/analytics/execute/${templateId}`,
        data: {
          params: {
            idObject: selectedItemId,
            startDate: startTimestamp,
            endDate: endTimestamp,
            type: selectedItemType,
          },
          additionalParams,
        },
      })) as ITemplateRow[];
      setTemplates(json);
    },
    { success: false },
  );

  const handleExportDiagram = async (): Promise<void> => {
    if (values && templates?.length && selectedItemId && startTimestamp && endTimestamp && selectedItemType) {
      const files: IFile[] = [];
      chartsRef.current?.forEach(value => {
        files.push(value);
      });
      const date = new Date().toISOString().split("T")[0];
      const name = `analytics-station-charts-report-${selectedItemId}-${date}.zip`;
      await downloadZip(files, name);
    }
  };

  const handleExportTable = async () => {
    if (!templates) {
      return;
    }

    const date = new Date().toISOString().split("T")[0];
    const files = templates
      .filter(template => template.data?.length)
      .map(template => {
        const content = json2csv(
          template.data!.map(template => {
            const result: any = {
              DiagramName: template.DiagramName || "-",
              Value: template.Value || "-",
            };
            if (template.AlarmStatus) {
              result.AlarmStatus = template.AlarmStatus;
            }
            if (template.LayerName) {
              result.LayerName = template.GrpLayerName1
                ? `${template.GrpLayerName1} - ${template.LayerName}`
                : template.LayerName;
            }
            if (template.ValueName) {
              result.ValueName = template.ValueName;
            }
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            return result;
          }),
          {
            excelBOM: true,
            delimiter: EXCEL_CSV_DELIMITER,
            keys: [
              {
                field: "AlarmStatus",
                title: formatMessage({ id: "pages.analytics.AlarmStatus" }),
                wildcardMatch: true,
              },
              { field: "DiagramName", title: formatMessage({ id: "pages.analytics.DiagramName" }) },
              { field: "LayerName", title: formatMessage({ id: "pages.analytics.LayerName" }), wildcardMatch: true },
              { field: "Value", title: formatMessage({ id: "pages.analytics.Value" }) },
              { field: "ValueName", title: formatMessage({ id: "pages.analytics.ValueName" }), wildcardMatch: true },
            ],
          },
        );
        const name = `analytics-data-report-${template.block.header}-${selectedItemId}-${date}.csv`;
        const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
        return { name, input: blob };
      });

    await downloadZip(
      files,
      `analytics-data-report${
        initialValues?.templateId ? `-${initialValues.templateId}` : ""
      }-${selectedItemId}-${date}.zip`,
    );
  };

  const handleInitialValues = (json: ITemplate, selectedTemplateId?: string | number) => {
    const values: any = {};

    const controls = json.control ? (Array.isArray(json.control) ? json.control : [json.control]) : [];

    const cap = (s?: string) => (s ? `${s.charAt(0).toUpperCase()}${s.slice(1)}` : "");

    const handlers: Partial<Record<RowControlType, (c: (typeof controls)[number]) => any>> = {
      [RowControlType.droplist]: c => c.__default ?? c.item?.[0]?.__val ?? "",
      [RowControlType.multiselect]: c => (c.__default ? [c.__default] : c.item?.[0]?.__val ? [c.item[0].__val] : []),
      [RowControlType.dropdown]: c => {
        const found = c.__default ? c.item?.find(i => i.__val === c.__default) : undefined;
        const title = found ? found["#text"] : c.item?.[0]?.["#text"];
        return title ? cap(title) : "";
      },
      [RowControlType.edit_int]: c => {
        const n = c.__default ? Number(c.__default) : 0;
        return Number.isNaN(n) ? 0 : n;
      },
      [RowControlType.edit_text]: c => c.__default ?? "",
      [RowControlType.edit_real]: c => c.__default ?? "",
    };

    controls.forEach(control => {
      values[control.__name] = handlers[control.__type as RowControlType]?.(control) ?? control.__default ?? "";
    });

    const tid = selectedTemplateId !== void 0 ? String(selectedTemplateId) : undefined;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    setInitialValues(prevValues => ({ ...values, templateId: tid ?? prevValues.templateId }));
  };

  const { fn: getTemplateApi, isLoading: isTemplateLoading } = useApiCall<void>(
    async (api, templateId: number) => {
      setTemplates(null);
      setTemplate(null);

      try {
        const json = (await api.fetchJson({
          url: `/template/analytics/${templateId}`,
          data: {
            params: {
              idObject: selectedItemId,
              startDate: startTimestamp,
              endDate: endTimestamp,
              type: selectedItemType,
            },
          },
        })) as ITemplate;
        if (json) {
          setTemplate(json);
          handleInitialValues(json, templateId);
        }
      } catch (error) {
        console.error(error);
      }
    },
    { error: false, success: false },
  );

  const getTemplate = (templateId: number) => {
    return getTemplateApi(void 0, templateId);
  };

  const handleExecute = useDebouncedCallback(async (values: any) => {
    setValues(values);
    return executeQuery(void 0, values);
  }, 500);

  const handleSubmit = async (values: any) => {
    if (validateControls && !validateControls()) {
      return;
    }
    return handleExecute(values);
  };

  const noChartData = formatMessage({ id: "pages.scheme.analytics.noChartData" });

  const handleReset = () => {
    resetAnalyticsState();
  };

  useEffect(() => {
    if (values && templates?.length && selectedItemId && startTimestamp && endTimestamp && selectedItemType) {
      void handleSubmit(values);
    }
  }, [selectedItemId, endTimestamp, selectedItemType]);

  useEffect(() => {
    return () => handleReset();
  }, []);

  return (
    <ProgressOverlay isLoading={isLoading || isQueryExecuting}>
      {selectedItemId ? (
        <FormWrapper
          key={formContextKey}
          initialValues={initialValues}
          onSubmit={handleSubmit}
          showButtons={false}
          showPrompt={false}
        >
          <Root>
            <StyledSettingsWrapper>
              <StyledSettings>
                <StyledSettingsColumn>
                  <Box sx={{ width: "100%", maxWidth: 500, px: 0.5 }}>
                    <TemplateInput handleReset={handleReset} data={{ type: selectedItemType }} />
                  </Box>
                </StyledSettingsColumn>
                <ControlsInput
                  template={template}
                  getTemplate={getTemplate}
                  isLoading={isTemplateLoading}
                  onValidationChange={handleValidationChange}
                  onControlValueReset={handleControlValueReset}
                />
              </StyledSettings>
              <StyledSettingsButtons>
                <Box sx={{ mr: 2 }}>
                  <SubmitButton
                    disabled={isLoading || isQueryExecuting || isTemplateLoading || !initialValues.templateId}
                  />
                </Box>
                <ClearButton onReset={handleReset} />
                <Box sx={{ display: "flex", ml: 2 }}>
                  <AnalyticsExport
                    onDiagramExport={handleExportDiagram}
                    onTableExport={handleExportTable}
                    disabled={!templates?.length || isLoading || isQueryExecuting || isTemplateLoading}
                  />
                </Box>
              </StyledSettingsButtons>
            </StyledSettingsWrapper>
            {templates?.length ? (
              <StyledDisplayView>
                {templates?.some(({ data }) => data?.length) ? (
                  templates.map((template, idx) => {
                    const { block, data } = template;
                    let header = block.header;
                    if (header) {
                      const topNValue = values?.topN || "";
                      header = header.replace("#topN", topNValue.toString());
                    }
                    header = header ?? "";

                    if (!data?.length) {
                      return null;
                    }

                    return (
                      <Box
                        key={block.__ID || block.header || idx}
                        sx={{ mt: 2, mb: 2, textAlign: "center", width: "100%" }}
                      >
                        {header ? (
                          <Typography variant="h3" sx={{ mb: 1, maxWidth: "80%", mx: "auto", textWrap: "balance" }}>
                            {`${header.charAt(0).toUpperCase()}${header.slice(1)}`}
                          </Typography>
                        ) : null}
                        <Box
                          sx={{
                            p: 2,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "stretch",
                            width: "100%",
                            maxWidth: "100%",
                            gap: 3,
                            justifyContent: "flex-start",
                          }}
                        >
                          <ViewSelector
                            data={data}
                            size={block.__Size || ""}
                            type={block.__type}
                            yLabel={block.YLabel || ""}
                            chartsRef={chartsRef}
                            header={header}
                          />
                        </Box>
                      </Box>
                    );
                  })
                ) : (
                  <EmptyPlaceholder icon={<AnalyticsIcon />} text={noChartData} />
                )}
              </StyledDisplayView>
            ) : null}
          </Root>
        </FormWrapper>
      ) : null}
    </ProgressOverlay>
  );
};
